import api from '@/utils/api';

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

interface TriggerRule {
  id?: string;
  trigger_name: string;
  trigger_event: string;
  template_id: string;
  trigger_conditions: any;
  parameter_mapping: Record<string, string>;
  channel: string;
  is_active: boolean;
  priority?: number;
}

/**
 * Generic Automation Service for all integration providers
 * Works with WATI, AiSensy, and any future integrations
 */
class AutomationService {
  private provider: string;

  constructor(provider: 'wati' | 'aisensy' | 'brevo') {
    this.provider = provider;
  }

  /**
   * Get automation triggers for the provider
   */
  async getTriggers(): Promise<ApiResponse<TriggerRule[]>> {
    try {
      const response = await api.req<TriggerRule[]>({
        url: `/integrations/${this.provider}/triggers`,
        method: api.Method.GET,
      });
      return response as ApiResponse<TriggerRule[]>;
    } catch (error) {
      console.error(`Error getting ${this.provider} triggers:`, error);
      throw error;
    }
  }

  /**
   * Create new automation trigger
   */
  async createTrigger(trigger: TriggerRule): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: `/integrations/${this.provider}/triggers`,
        method: api.Method.POST,
        body: trigger
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error(`Error creating ${this.provider} trigger:`, error);
      throw error;
    }
  }

  /**
   * Update existing automation trigger
   */
  async updateTrigger(triggerId: string, trigger: TriggerRule): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: `/integrations/${this.provider}/triggers`,
        method: api.Method.POST,
        body: { ...trigger, id: triggerId }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error(`Error updating ${this.provider} trigger:`, error);
      throw error;
    }
  }

  /**
   * Delete automation trigger
   */
  async deleteTrigger(triggerId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: `/integrations/${this.provider}/triggers/${triggerId}`,
        method: api.Method.DELETE,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error(`Error deleting ${this.provider} trigger:`, error);
      throw error;
    }
  }

  /**
   * Toggle trigger status (active/inactive)
   */
  async toggleTriggerStatus(triggerId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: `/integrations/${this.provider}/triggers/${triggerId}/toggle`,
        method: api.Method.PATCH,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error(`Error toggling ${this.provider} trigger status:`, error);
      throw error;
    }
  }

  /**
   * Get CRM lead columns for parameter mapping
   */
  async getCrmLeadColumns(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: '/integrations/crm/lead-columns',
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error getting CRM lead columns:', error);
      throw error;
    }
  }

  /**
   * Get status options for triggers
   */
  async getStatusOptions(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req({
        url: '/integrations/crm/status-options',
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error getting status options:', error);
      throw error;
    }
  }
}

// Export instances for each provider
export const watiAutomationService = new AutomationService('wati');
export const aiSensyAutomationService = new AutomationService('aisensy');

export default AutomationService;